import React, { useEffect } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, useNavigate, useLocation } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from "components/ProtectedRoute";
import PageTransition from "components/PageTransition";
import ThemeProvider from "contexts/ThemeContext";
import AdminDashboard from "pages/admin-dashboard";
import AdminLoginScreen from "pages/admin-login-screen";
import OnTheGoLandingPage from "pages/on-the-go-landing-page";
import CustomerBookingScreen from "pages/customer-booking-screen";
import OrderTrackingScreen from "pages/order-tracking-screen";
import DriverDashboardScreen from "pages/driver-dashboard-screen";
import DispatcherConsoleScreen from "pages/dispatcher-console-screen";
import DriverOnboardingScreen from "pages/driver-onboarding-screen";
import PaymentsWalletScreen from "pages/payments-wallet-screen";
import PromotionsEngineScreen from "pages/promotions-engine-screen";
import DisputeSupportScreen from "pages/dispute-support-screen";
import DriverLoginScreen from "pages/driver-login-screen";
import CustomerLoginScreen from "pages/customer-login-screen";
import FleetFinanceScreen from "pages/fleet-finance-and-crowdfunding-screen";
import CustomerProfileScreen from "pages/customer-profile-screen";
import DriverProfileScreen from "pages/driver-profile-screen";
import BookingConfirmationScreen from "pages/booking-confirmation-screen";
import RatingSubmissionScreen from "pages/rating-submission-screen";
import RatingsReviewScreen from "pages/ratings-review-screen";
// Car Rental + Corporate Leasing Module
import CarHireListingScreen from "pages/car-hire-listing";
import CarDetailScreen from "pages/car-detail";
import RentalBookingCheckoutScreen from "pages/rental-booking-checkout";
import MyRentalsScreen from "pages/my-rentals";
import ListMyCarScreen from "pages/list-my-car";
import MyVehiclesScreen from "pages/my-vehicles";
import BookingRequestsScreen from "pages/booking-requests";
import CorporateLeasingScreen from "pages/corporate-leasing";
import LeaseRequestScreen from "pages/lease-request";
import CorporateDashboardScreen from "pages/corporate-dashboard";
import VehicleReviewQueueScreen from "pages/vehicle-review-queue";
import RentalPricingPanelScreen from "pages/rental-pricing-panel";
import RentalBookingOversightScreen from "pages/rental-booking-oversight";
import CorporateContractsScreen from "pages/corporate-contracts";
import HostDashboard from "pages/host-dashboard";
import HostSignupScreen from "pages/host-signup";
import SetupPasswordPage from "pages/auth-setup-password";
import ResetPasswordPage from "pages/auth-reset-password";
import NotFound from "pages/NotFound";

// Handles redirect when user lands with an invite/recovery token in the URL hash
const InviteTokenHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = window.location?.hash;
    if (!hash) return;

    const params = new URLSearchParams(hash.replace('#', ''));
    const accessToken = params?.get('access_token');
    const type = params?.get('type');

    if (!accessToken) return;

    // Recovery tokens → dedicated reset-password page
    if (type === 'recovery') {
      if (location?.pathname !== '/auth/reset-password') {
        navigate('/auth/reset-password' + window.location?.hash, { replace: true });
      }
    // Invite/signup tokens → setup-password page
    } else if (type === 'invite' || type === 'signup') {
      if (location?.pathname !== '/auth/setup-password') {
        navigate('/auth/setup-password' + window.location?.hash, { replace: true });
      }
    }
  }, []);

  return null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <ErrorBoundary>
          <ScrollToTop />
          <InviteTokenHandler />
          <PageTransition>
            <RouterRoutes>
              {/* Public routes */}
              <Route path="/" element={<OnTheGoLandingPage />} />
              <Route path="/on-the-go-landing-page" element={<OnTheGoLandingPage />} />

              {/* Auth routes */}
              <Route path="/auth/setup-password" element={<SetupPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/admin-login" element={<AdminLoginScreen />} />
              <Route path="/admin-login-screen" element={<AdminLoginScreen />} />
              <Route path="/driver-login-screen" element={<DriverLoginScreen />} />
              <Route path="/customer-login-screen" element={<CustomerLoginScreen />} />

              {/* Driver Onboarding - public so new drivers can register */}
              <Route path="/driver-onboarding-screen" element={<DriverOnboardingScreen />} />

              {/* Customer routes - accessible without auth (guest booking supported) */}
              <Route path="/customer-booking-screen" element={<CustomerBookingScreen />} />
              <Route path="/order-tracking-screen" element={<OrderTrackingScreen />} />
              <Route path="/booking-confirmation-screen" element={<BookingConfirmationScreen />} />
              <Route path="/fleet-finance-and-crowdfunding-screen" element={<FleetFinanceScreen />} />
              <Route path="/rating-submission-screen" element={<RatingSubmissionScreen />} />
              <Route path="/ratings-review-screen" element={<RatingsReviewScreen />} />

              {/* ─── CAR RENTAL MODULE ─── */}
              {/* Customer Rental Routes */}
              <Route path="/car-hire-listing" element={<CarHireListingScreen />} />
              <Route path="/car-detail" element={<CarDetailScreen />} />
              <Route path="/rental-booking-checkout" element={<RentalBookingCheckoutScreen />} />
              <Route path="/my-rentals" element={<MyRentalsScreen />} />

              {/* Owner/Host Routes */}
              <Route path="/list-my-car" element={<ListMyCarScreen />} />
              <Route path="/my-vehicles" element={<MyVehiclesScreen />} />
              <Route path="/booking-requests" element={<BookingRequestsScreen />} />
              <Route path="/host-dashboard" element={<HostDashboard />} />
              <Route path="/host-signup" element={<HostSignupScreen />} />

              {/* Corporate Leasing Routes */}
              <Route path="/corporate-leasing" element={<CorporateLeasingScreen />} />
              <Route path="/lease-request" element={<LeaseRequestScreen />} />
              <Route path="/corporate-dashboard" element={<CorporateDashboardScreen />} />

              {/* Admin Rental Routes */}
              <Route
                path="/vehicle-review-queue"
                element={
                  <ProtectedRoute redirectTo="/admin-login">
                    <VehicleReviewQueueScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rental-pricing-panel"
                element={
                  <ProtectedRoute redirectTo="/admin-login">
                    <RentalPricingPanelScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rental-booking-oversight"
                element={
                  <ProtectedRoute redirectTo="/admin-login">
                    <RentalBookingOversightScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/corporate-contracts"
                element={
                  <ProtectedRoute redirectTo="/admin-login">
                    <CorporateContractsScreen />
                  </ProtectedRoute>
                }
              />

              {/* Profile routes */}
              <Route path="/customer-profile-screen" element={<CustomerProfileScreen />} />
              <Route
                path="/driver-profile-screen"
                element={
                  <ProtectedRoute redirectTo="/driver-login-screen">
                    <DriverProfileScreen />
                  </ProtectedRoute>
                }
              />

              {/* Driver protected routes */}
              <Route
                path="/driver-dashboard-screen"
                element={
                  <ProtectedRoute redirectTo="/driver-login-screen">
                    <DriverDashboardScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/payments-wallet-screen"
                element={
                  <ProtectedRoute redirectTo="/driver-login-screen">
                    <PaymentsWalletScreen />
                  </ProtectedRoute>
                }
              />

              {/* Admin protected routes */}
              <Route
                path="/admin-dashboard"
                element={
                  <ProtectedRoute redirectTo="/admin-login" requireAdmin={true}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dispatcher-console-screen"
                element={
                  <ProtectedRoute redirectTo="/admin-login" requireAdmin={true}>
                    <DispatcherConsoleScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/promotions-engine-screen"
                element={
                  <ProtectedRoute redirectTo="/admin-login" requireAdmin={true}>
                    <PromotionsEngineScreen />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dispute-support-screen"
                element={
                  <ProtectedRoute redirectTo="/admin-login" requireAdmin={true}>
                    <DisputeSupportScreen />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<NotFound />} />
            </RouterRoutes>
          </PageTransition>
        </ErrorBoundary>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default Routes;